import { useState } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  Plus,
  Search,
  Mail,
  Building2,
  ChevronRight,
  AlertCircle,
  Loader2,
  CheckSquare,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";

const API_BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface ContactSummary {
  id: string;
  name: string;
  email: string;
  company: string | null;
  stage: string;
  assignedTo: string | null;
  notes: string | null;
  openTaskCount: number;
  lastMailAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const STAGE_LABELS: Record<string, string> = {
  prospect: "Prospect",
  active: "En cours",
  client: "Client",
  lost: "Perdu",
};

const STAGE_COLORS: Record<string, string> = {
  prospect: "bg-blue-100 text-blue-700",
  active: "bg-amber-100 text-amber-700",
  client: "bg-green-100 text-green-700",
  lost: "bg-gray-100 text-gray-500",
};

const STAGES = ["all", "prospect", "active", "client", "lost"] as const;
type StageFilter = (typeof STAGES)[number];

async function fetchContacts(stage: string): Promise<ContactSummary[]> {
  const params = stage !== "all" ? `?stage=${stage}` : "";
  const res = await fetch(`${API_BASE}/api/crm/contacts${params}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("Erreur lors du chargement des contacts");
  return res.json() as Promise<ContactSummary[]>;
}

async function createContact(body: {
  name: string;
  email: string;
  company?: string;
  stage: string;
  assignedTo?: string;
}): Promise<ContactSummary> {
  const res = await fetch(`${API_BASE}/api/crm/contacts`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error("Erreur lors de la création du contact");
  return res.json() as Promise<ContactSummary>;
}

function StageFilterTabs({
  value,
  onChange,
}: {
  value: StageFilter;
  onChange: (v: StageFilter) => void;
}) {
  return (
    <div className="flex gap-1 flex-wrap">
      {STAGES.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === s
              ? "bg-primary text-primary-foreground"
              : "bg-muted text-muted-foreground hover:bg-muted/80"
          }`}
        >
          {s === "all" ? "Tous" : STAGE_LABELS[s]}
        </button>
      ))}
    </div>
  );
}

function CreateContactDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [stage, setStage] = useState("prospect");
  const [assignedTo, setAssignedTo] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["crm-contacts"] });
      toast({ title: "Contact créé" });
      setName("");
      setEmail("");
      setCompany("");
      setStage("prospect");
      setAssignedTo("");
      onClose();
    },
    onError: () => {
      toast({ title: "Erreur", description: "Impossible de créer le contact", variant: "destructive" });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    mutation.mutate({
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
      stage,
      assignedTo: assignedTo.trim() || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nouveau contact</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label>Nom *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jean Dupont"
              required
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jean@xyz.fr"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Société</Label>
            <Input
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="XYZ Corp"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Stade</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prospect">Prospect</SelectItem>
                  <SelectItem value="active">En cours</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="lost">Perdu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Assigné à</Label>
              <Input
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
                placeholder="Prénom"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Annuler
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ContactCard({ contact }: { contact: ContactSummary }) {
  const daysSinceMail = contact.lastMailAt
    ? Math.floor(
        (Date.now() - new Date(contact.lastMailAt).getTime()) / 86_400_000,
      )
    : null;
  const needsFollowUp = daysSinceMail !== null && daysSinceMail >= 7;

  return (
    <Link href={`/app/crm/${contact.id}`}>
      <Card className="cursor-pointer hover:shadow-md transition-shadow border border-border/60">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-sm font-semibold text-primary">
              {contact.name.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm truncate">{contact.name}</span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${STAGE_COLORS[contact.stage] ?? "bg-gray-100 text-gray-600"}`}
              >
                {STAGE_LABELS[contact.stage] ?? contact.stage}
              </span>
              {needsFollowUp && (
                <span className="flex items-center gap-1 text-xs text-amber-600 font-medium">
                  <AlertCircle className="w-3 h-3" />
                  Relance {daysSinceMail}j
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-col gap-0.5">
              {contact.company && (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {contact.company}
                </span>
              )}
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {contact.email}
              </span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
              {contact.openTaskCount > 0 && (
                <span className="flex items-center gap-1">
                  <CheckSquare className="w-3 h-3" />
                  {contact.openTaskCount} tâche{contact.openTaskCount > 1 ? "s" : ""}
                </span>
              )}
              {contact.lastMailAt && (
                <span>
                  Dernier mail{" "}
                  {formatDistanceToNow(new Date(contact.lastMailAt), {
                    locale: fr,
                    addSuffix: true,
                  })}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-1" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ContactsPage() {
  const [stageFilter, setStageFilter] = useState<StageFilter>("all");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ["crm-contacts", stageFilter],
    queryFn: () => fetchContacts(stageFilter),
  });

  const filtered = (contacts ?? []).filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.company ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">CRM Contacts</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Gérez vos prospects, clients et opportunités.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Nouveau contact
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher…"
            className="pl-9"
          />
        </div>
        <StageFilterTabs value={stageFilter} onChange={setStageFilter} />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Users className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <p className="text-sm text-muted-foreground">
            {contacts?.length === 0
              ? "Aucun contact pour l'instant. Créez-en un !"
              : "Aucun contact ne correspond à votre recherche."}
          </p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((c) => (
            <ContactCard key={c.id} contact={c} />
          ))}
        </div>
      )}

      <CreateContactDialog open={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
